import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { clearLocalStorageKeepingPolicyNotices } from "./storage";

const IMPERSONATION_STORAGE_KEY = "imp";

function getValidImpersonationToken(): any | null {
    const json = localStorage.getItem(IMPERSONATION_STORAGE_KEY);

    if (!json) {
        return null;
    }

    try {
        const token = JSON.parse(json);
        const expiresAt = token.expires_at ?? token.expirateAt;

        if (expiresAt && Number(expiresAt) <= Math.floor(Date.now() / 1000)) {
            localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
            return null;
        }

        return token;
    } catch {
        localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
        return null;
    }
}

function getAuthConfig(): AuthConfig {
    return {
        authority: process.env.REACT_APP_AUTHORITY!,
        clientId: process.env.REACT_APP_CLIENT_ID!,
        callbackUrl: `${window.location.origin}/auth-callback`,
        silentCallbackUrl: `${window.location.origin}/auth-silent-callback`,
        logoutUrl: `${window.location.origin}`,
        scope: process.env.REACT_APP_SCOPE ?? "openid profile",
    };
}

export interface AuthConfig {
    authority: string;
    callbackUrl: string;
    silentCallbackUrl: string;
    logoutUrl: string;
    clientId: string;
    scope: string;
}

class AuthManager {
    readonly userManager: UserManager;

    constructor() {
        const config = getAuthConfig();
        this.userManager = new UserManager({
            authority: config.authority,
            client_id: config.clientId,
            redirect_uri: config.callbackUrl,
            silent_redirect_uri: config.silentCallbackUrl,
            post_logout_redirect_uri: config.logoutUrl,
            response_type: "code",
            scope: config.scope,
            userStore: new WebStorageStateStore({ store: window.localStorage }),
            automaticSilentRenew: true,
        });

        this.userManager.startSilentRenew();

        this.userManager.events.addAccessTokenExpiring(() => {
            console.log(
                "OIDC access token is expiring; attempting silent renew."
            );
        });
        this.userManager.events.addAccessTokenExpired(() => {
            console.log("OIDC access token expired.");
        });
        this.userManager.events.addSilentRenewError((error) => {
            console.error("OIDC silent renew failed:", error);
        });
        this.userManager.events.addUserLoaded((user) => {
            console.log(
                "OIDC user loaded/refreshed. expires_in:",
                user.expires_in,
                "expires_at:",
                user.expires_at
            );
        });
    }

    async getAccessToken(): Promise<string> {
        const impersonationToken = getValidImpersonationToken();

        if (impersonationToken) {
            return impersonationToken.access_token;
        } else {
            const user = await this.userManager.getUser();
            return user?.access_token ?? "";
        }
    }

    async initialize(extraQueryParams?: Record<string, string>): Promise<void> {
        const user = await this.userManager.getUser();
        if (user && !user.expired) {
            return;
        }

        localStorage.setItem(
            "redirect-url",
            window.location.pathname + window.location.search
        );
        await this.userManager.signinRedirect({ extraQueryParams });
        return null;
    }

    completeAuthentication(
        onSuccess: (user: User) => void,
        onError: (error: unknown) => void
    ) {
        this.userManager
            .signinCallback()
            .then((user) => onSuccess(user))
            .catch(onError);
    }

    async logout() {
        await this.userManager.signoutRedirect();
        clearLocalStorageKeepingPolicyNotices();
    }

    async completeSilentAuthentication() {
        await this.userManager.signinSilentCallback();
    }

    get isImpersonated(): boolean {
        return !!getValidImpersonationToken();
    }

    async exchangeTicket(ticket: string) {
        const tokenEndpoint =
            await authManager.userManager.metadataService.getTokenEndpoint(
                false
            );
        const config = getAuthConfig();

        const body = new URLSearchParams({
            grant_type: "ticket",
            client_id: config.clientId,
            // Your current ticket is an opaque/cache ticket, so use a private token type.
            code: ticket,
        });

        const response = await fetch(tokenEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body,
        });

        if (!response.ok) {
            throw new Error(await response.text());
        }

        const token = await response.json();

        localStorage.setItem(
            IMPERSONATION_STORAGE_KEY,
            JSON.stringify({
                ...token,
                expires_at: Math.floor(Date.now() / 1000) + token.expires_in,
            })
        );
    }
}

let authManager: AuthManager | null = null;

export function getAuthManager(): AuthManager {
    if (!authManager) {
        authManager = new AuthManager();
    }

    return authManager;
}
